import React, { useEffect } from 'react';
import { compose } from 'recompose';
import { ErrorBoundary } from 'react-error-boundary';
import { withRouter, Switch, Redirect, Route } from 'react-router-dom';
import NotificationContainer from 'common/NotificationContainer';
import RequireAuth from './RequireAuth';
import { Login, Authorize, Confirm, Register } from '../scenes/Login';
import CarePlan from 'scenes/CarePlan';
import Progress from 'scenes/Progress';
import Education, { Content } from 'scenes/Education';
import History from 'scenes/History';
import Messages from 'scenes/Messages';
import Settings from 'scenes/Settings';
import Task from 'scenes/TaskDetail';
import Questionnaire, { CompletedQuestionnaire } from 'scenes/Questionnaire';
import SignatureHandler from '../scenes/SignatureHandler/SignatureHandler';
import QRTask from 'scenes/QRTask';
import NPS from 'scenes/NPS';
import Error404 from 'common/Error404';
import ErrorPage from 'common/ErrorPage';

const SITE_TITLE = 'Keet Health';

/** Maps URL to a screen-reader-friendly document title (section | Keet Health). */
function documentTitleForPath(pathname) {
  const withSite = (page) => `${page} | ${SITE_TITLE}`;

  if (pathname === '/' || pathname === '/login') return withSite('Log In');
  if (pathname === '/register') return withSite('Register');
  if (pathname === '/authorize') return withSite('Authorize');
  if (pathname.startsWith('/confirm/')) return withSite('Confirm');

  if (pathname.startsWith('/qr_task/') || pathname.startsWith('/qr_tasks/')) {
    return withSite('Questionnaire');
  }
  if (pathname.startsWith('/nps_survey')) return withSite('Nps');
  if (pathname.startsWith('/nps/')) return withSite('Nps');

  if (pathname.startsWith('/my_tasks')) return withSite('My Tasks');

  if (pathname === '/progress') return withSite('Progress');

  if (pathname === '/education' || pathname.startsWith('/education/')) {
    return withSite('Education');
  }

  if (pathname === '/history') return withSite('History');
  if (pathname === '/messages') return withSite('Messages');
  if (pathname === '/settings') return withSite('Settings');

  if (pathname.startsWith('/exercise/')) return withSite('Exercise');
  if (pathname.startsWith('/questionnaire/')) return withSite('Questionnaire');
  if (pathname.startsWith('/completed_questionnaire/')) {
    return withSite('Questionnaire');
  }

  return withSite('Page Not Found');
}

function AppBody({ location }) {
  useEffect(() => {
    document.body.dataset.theme = process.env.REACT_APP_THEME || 'keet';
  }, []);

  useEffect(() => {
    if (location?.pathname != null) {
      document.title = documentTitleForPath(location.pathname);
    }
  }, [location?.pathname]);

  return (
    <ErrorBoundary FallbackComponent={ErrorPage} onError={(error, info) => {}}>
      <Switch>
        {/* PUBLIC */}
        <Route path="/" exact render={() => <Redirect to="/login" />} />
        <Route path="/authorize" exact component={Authorize} />
        <Route path="/confirm/:id" exact component={Confirm} />
        <Route path="/login" exact component={Login} />
        <Route path="/register" exact component={Register} />
        <Route
          path="/qr_task/:resourceId/:signature?"
          exact
          render={(props) => (
            <SignatureHandler {...props} path="qr_tasks" suffix="0" />
          )}
        />
        <Route
          path="/qr_tasks/:taskId/:questionIndex?"
          exact
          component={QRTask}
        />
        <Route
          path="/nps/:resourceId"
          exact
          render={(props) => <SignatureHandler {...props} path="nps_survey" />}
        />
        <Route path="/nps_survey/:npsId" exact component={NPS} />
        {/* PRIVATE */}
        <Route
          path="/my_tasks/:taskId/:questionIndex?"
          exact
          render={(props) => (
            <RequireAuth>
              <CarePlan {...props} />
            </RequireAuth>
          )}
        />
        <Route
          path="/my_tasks"
          exact
          render={(props) => (
            <RequireAuth>
              <CarePlan {...props} />
            </RequireAuth>
          )}
        />
        <Route
          path="/progress"
          exact
          render={(props) => (
            <RequireAuth>
              <Progress {...props} />
            </RequireAuth>
          )}
        />
        <Route
          path="/education"
          exact
          render={(props) => (
            <RequireAuth>
              <Education {...props} />
            </RequireAuth>
          )}
        />
        <Route
          path="/education/:taskId"
          exact
          render={(props) => (
            <RequireAuth>
              <Content {...props} />
            </RequireAuth>
          )}
        />
        <Route
          path="/history"
          exact
          render={(props) => (
            <RequireAuth>
              <History {...props} />
            </RequireAuth>
          )}
        />
        <Route
          path="/messages"
          exact
          render={(props) => (
            <RequireAuth>
              <Messages {...props} />
            </RequireAuth>
          )}
        />
        <Route
          path="/settings"
          exact
          render={(props) => (
            <RequireAuth>
              <Settings {...props} />
            </RequireAuth>
          )}
        />
        <Route
          path="/exercise/:taskId"
          exact
          render={(props) => (
            <RequireAuth>
              <Task {...props} />
            </RequireAuth>
          )}
        />
        <Route
          path="/questionnaire/:questionnaireId"
          exact
          render={(props) => (
            <RequireAuth>
              <Questionnaire {...props} />
            </RequireAuth>
          )}
        />
        <Route
          path="/completed_questionnaire/:taskId"
          exact
          render={(props) => (
            <RequireAuth>
              <CompletedQuestionnaire {...props} />
            </RequireAuth>
          )}
        />
        {/* 404 */}
        <Route component={Error404} />
      </Switch>
      <NotificationContainer />
    </ErrorBoundary>
  );
}

export default compose(withRouter)(AppBody);
